import { Box, Button, Skeleton, Tooltip, Typography } from "@mui/material";

import { TILE_CAPTION_SX, TILE_CARD_SX } from "./tileStyles";
import type { GeneratedItem } from "@/features/Generations/types/generatedItem";

import {
  CheckCircleIcon,
  ErrorIcon,
  ImageOutlinedIcon,
  InfoOutlinedIcon,
} from "@/icons";

export type GeneratedTileProps = {
  item: GeneratedItem;
  accepted: boolean;
  onToggle: () => void;
};

export function GeneratedTile({
  item,
  accepted,
  onToggle,
}: GeneratedTileProps) {
  const failed = item.status === "failed";

  return (
    <Box
      data-testid="generated-tile"
      data-status={item.status}
      data-accepted={failed ? undefined : accepted}
      sx={TILE_CARD_SX}
    >
      {failed ? (
        <FailedPreview item={item} />
      ) : (
        <AcceptToggle item={item} accepted={accepted} onToggle={onToggle} />
      )}

      <Box sx={TILE_CAPTION_SX}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {item.sequence} · {item.sku}
        </Typography>

        {failed ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {item.failureReason}
            </Typography>
            <Tooltip title={item.failureReason ?? ""}>
              <Box
                sx={{
                  color: "text.secondary",
                  display: "inline-flex",
                  flexShrink: 0,
                }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </Box>
            </Tooltip>
          </Box>
        ) : (
          <Button
            size="small"
            onClick={onToggle}
            data-testid="generated-tile-toggle"
            sx={{ alignSelf: "flex-start", px: 0, minWidth: 0 }}
          >
            {accepted ? "Remove" : "Add back"}
          </Button>
        )}
      </Box>
    </Box>
  );
}

function AcceptToggle({ item, accepted, onToggle }: GeneratedTileProps) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onToggle}
      aria-pressed={accepted}
      aria-label={`${accepted ? "Remove" : "Add back"} ${item.sequence} ${item.sku}`}
      sx={(theme) => ({
        position: "relative",
        p: 0,
        border: 0,
        cursor: "pointer",
        width: "100%",
        display: "block",
        aspectRatio: "4 / 3",
        // Rejected items dim so accepted ones stand out at a glance.
        opacity: accepted ? 1 : 0.4,
        transition: theme.transitions.create("opacity"),
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: -2,
        },
      })}
    >
      {item.imageUrl ? (
        <Box
          component="img"
          src={item.imageUrl}
          alt={`${item.sequence} ${item.sku}`}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : item.color ? (
        <Box
          aria-hidden
          sx={{ width: "100%", height: "100%", bgcolor: item.color }}
        />
      ) : (
        <Skeleton
          aria-hidden
          variant="rectangular"
          data-testid="generated-tile-skeleton"
          sx={{ width: "100%", height: "100%" }}
        />
      )}

      {accepted && (
        <Box
          aria-hidden
          data-testid="generated-tile-accepted-badge"
          sx={(theme) => ({
            position: "absolute",
            top: 8,
            right: 8,
            display: "inline-flex",
            borderRadius: "50%",
            color: theme.palette.primary.main,
            bgcolor: theme.palette.background.paper,
          })}
        >
          <CheckCircleIcon fontSize="small" />
        </Box>
      )}
    </Box>
  );
}

function FailedPreview({ item }: { item: GeneratedItem }) {
  return (
    <Box
      data-testid="generated-tile-failed"
      sx={{
        position: "relative",
        aspectRatio: "4 / 3",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "action.hover",
      }}
    >
      {item.imageUrl ? (
        <Box
          component="img"
          src={item.imageUrl}
          alt={`${item.sequence} ${item.sku}`}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            opacity: 0.5,
          }}
        />
      ) : (
        <ImageOutlinedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
      )}

      <Box
        aria-hidden
        data-testid="generated-tile-error-icon"
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          display: "inline-flex",
          borderRadius: "50%",
          color: "error.main",
          bgcolor: "background.paper",
        }}
      >
        <ErrorIcon fontSize="small" />
      </Box>
    </Box>
  );
}
